import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, Eye, FileText, Users, Database, Mail, ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/90 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Zurück</span>
          </button>
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-10 h-10" />
            <h1 className="text-4xl font-bold">Datenschutzerklärung</h1>
          </div>
          <p className="text-blue-100 text-lg">
            Informationen zur Datenverarbeitung gemäß DSGVO
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Introduction */}
        <section className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <FileText className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">1. Datenschutz auf einen Blick</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Allgemeine Hinweise</h3>
              <p className="text-gray-700 leading-relaxed">
                Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen 
                Daten passiert, wenn Sie unsere Website besuchen oder unsere Dienste nutzen. Personenbezogene 
                Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können. Ausführliche 
                Informationen zum Thema Datenschutz entnehmen Sie unserer unter diesem Text aufgeführten 
                Datenschutzerklärung.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Datenerfassung auf dieser Website</h3>
              <p className="text-gray-700 leading-relaxed mb-2">
                <strong>Wer ist verantwortlich für die Datenerfassung auf dieser Website?</strong>
              </p>
              <p className="text-gray-700 leading-relaxed">
                Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten 
                können Sie dem Abschnitt "Verantwortlicher" in dieser Datenschutzerklärung entnehmen.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Wie erfassen wir Ihre Daten?</h3>
              <p className="text-gray-700 leading-relaxed">
                Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich 
                z.B. um Daten handeln, die Sie in ein Kontaktformular oder bei der Registrierung eingeben. 
                Andere Daten werden automatisch oder nach Ihrer Einwilligung beim Besuch der Website durch unsere 
                IT-Systeme erfasst. Das sind vor allem technische Daten (z.B. Internetbrowser, Betriebssystem 
                oder Uhrzeit des Seitenaufrufs).
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Wofür nutzen wir Ihre Daten?</h3>
              <p className="text-gray-700 leading-relaxed">
                Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu gewährleisten. 
                Andere Daten können zur Analyse Ihres Nutzerverhaltens verwendet werden. Im Rahmen des THG 
                Premium Manager Systems werden Ihre Daten zur Bearbeitung von THG-Quoten-Anträgen und zur 
                Kommunikation mit Ihnen verwendet.
              </p>
            </div>
          </div>
        </section>

        {/* Responsible Party */}
        <section className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <Users className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">2. Verantwortlicher und Datenschutzbeauftragter</h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Verantwortlicher</h3>
              <p className="text-gray-700 leading-relaxed">
                Verantwortlich für die Datenverarbeitung auf dieser Website ist:<br /><br />
                <strong>Grid Balance UG</strong><br />
                Rosenhügel 4a<br />
                79599 Wittlingen<br />
                Deutschland<br /><br />
                Telefon: +49 152 23152476<br />
                E-Mail: oliver.wagner@gridbalance.de
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Datenschutzbeauftragter</h3>
              <p className="text-gray-700 leading-relaxed">
                Bei Fragen zum Datenschutz können Sie sich jederzeit an unseren Datenschutzbeauftragten wenden:<br /><br />
                Oliver Wagner<br />
                E-Mail: oliver.wagner@gridbalance.de<br />
                Telefon: +49 152 23152476
              </p>
            </div>
          </div>
        </section>

        {/* Legal Basis */}
        <section className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <FileText className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">3. Rechtsgrundlagen der Verarbeitung</h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Art. 6 Abs. 1 DSGVO</h3>
              <p className="text-gray-700 leading-relaxed mb-2">
                Wir verarbeiten personenbezogene Daten auf Grundlage folgender Rechtsgrundlagen:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>Art. 6 Abs. 1 lit. a DSGVO:</strong> Einwilligung der betroffenen Person</li>
                <li><strong>Art. 6 Abs. 1 lit. b DSGVO:</strong> Verarbeitung zur Erfüllung eines Vertrags oder vorvertraglicher Maßnahmen</li>
                <li><strong>Art. 6 Abs. 1 lit. c DSGVO:</strong> Verarbeitung zur Erfüllung einer rechtlichen Verpflichtung</li>
                <li><strong>Art. 6 Abs. 1 lit. f DSGVO:</strong> Verarbeitung zur Wahrung berechtigter Interessen</li>
              </ul>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Berechtigte Interessen</h3>
              <p className="text-gray-700 leading-relaxed">
                Soweit wir Ihre Daten auf Grundlage berechtigter Interessen verarbeiten, bestehen diese 
                insbesondere in der Bereitstellung und Verbesserung unserer Dienste, der Gewährleistung der 
                IT-Sicherheit sowie der Verhinderung von Missbrauch.
              </p>
            </div>
          </div>
        </section>

        {/* Data Collection */}
        <section className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <Database className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">4. Datenerfassung und -verarbeitung</h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">4.1 Registrierung und Antragsstellung</h3>
              <p className="text-gray-700 leading-relaxed mb-2">
                Bei der Registrierung für den THG Premium Manager und der Einreichung von Anträgen erfassen wir:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Vor- und Nachname</li>
                <li>E-Mail-Adresse</li>
                <li>Telefonnummer (optional)</li>
                <li>Adressdaten (Straße, PLZ, Ort)</li>
                <li>Fahrzeugdaten (Kennzeichen, Fahrzeug-Identifizierungsnummer, Hersteller, Modell)</li>
                <li>Bankverbindung (für Auszahlungen)</li>
                <li>Kopie der Zulassungsbescheinigung Teil I</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-2">
                <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)<br />
                <strong>Speicherdauer:</strong> Bis zur vollständigen Abwicklung des Antrags plus gesetzliche Aufbewahrungsfristen
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">4.2 Partner-Partnerschaften</h3>
              <p className="text-gray-700 leading-relaxed mb-2">
                Bei der Registrierung als Partner erfassen wir:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Firmenname und Rechtsform</li>
                <li>Geschäftsadresse</li>
                <li>Kontaktperson (Name, E-Mail, Telefon)</li>
                <li>Handelsregisternummer</li>
                <li>Umsatzsteuer-Identifikationsnummer</li>
                <li>Bankverbindung</li>
                <li>Logo und Branding-Materialien</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-2">
                <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)<br />
                <strong>Speicherdauer:</strong> Dauer der Geschäftsbeziehung plus gesetzliche Aufbewahrungsfristen
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">4.3 Server-Log-Dateien</h3>
              <p className="text-gray-700 leading-relaxed mb-2">
                Der Provider der Seiten erhebt und speichert automatisch Informationen in Server-Log-Dateien:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Browsertyp und Browserversion</li>
                <li>Verwendetes Betriebssystem</li>
                <li>Referrer URL</li>
                <li>Hostname des zugreifenden Rechners</li>
                <li>Uhrzeit der Serveranfrage</li>
                <li>IP-Adresse (anonymisiert)</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-2">
                <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an IT-Sicherheit)<br />
                <strong>Speicherdauer:</strong> 7 Tage
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">4.4 Cookies und Tracking</h3>
              <p className="text-gray-700 leading-relaxed">
                Unsere Website verwendet Cookies, um die Benutzerfreundlichkeit zu verbessern. Einige Cookies 
                sind technisch notwendig, andere dienen der Analyse des Nutzerverhaltens. Sie können Ihre 
                Cookie-Einstellungen jederzeit über unser Cookie-Banner anpassen.
              </p>
              <p className="text-gray-700 leading-relaxed mt-2">
                <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung) bzw. lit. f DSGVO (technisch notwendige Cookies)<br />
                <strong>Speicherdauer:</strong> Je nach Cookie-Typ zwischen Sitzungsende und 12 Monaten
              </p>
            </div>
          </div>
        </section>

        {/* Data Sharing */}
        <section className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <Users className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">5. Weitergabe von Daten</h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">5.1 Behörden und Quotenhändler</h3>
              <p className="text-gray-700 leading-relaxed">
                Zur Bearbeitung Ihres THG-Quoten-Antrags geben wir notwendige Daten an das Umweltbundesamt 
                und beauftragte Quotenhändler weiter. Dies umfasst insbesondere Fahrzeugdaten und 
                Zulassungsinformationen.
              </p>
              <p className="text-gray-700 leading-relaxed mt-2">
                <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">5.2 Auftragsverarbeiter</h3>
              <p className="text-gray-700 leading-relaxed mb-2">
                Wir setzen folgende Auftragsverarbeiter ein:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>Hosting:</strong> Supabase (USA) - EU-Standardvertragsklauseln</li>
                <li><strong>E-Mail-Versand:</strong> Eigener SMTP-Server (Deutschland) - DSGVO-konform</li>
                <li><strong>Dokumentenverarbeitung:</strong> OpenAI (USA) - EU-Standardvertragsklauseln</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-2">
                Mit allen Auftragsverarbeitern wurden Auftragsverarbeitungsverträge gemäß Art. 28 DSGVO geschlossen.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">5.3 Keine Weitergabe an Dritte</h3>
              <p className="text-gray-700 leading-relaxed">
                Eine Weitergabe Ihrer Daten an Dritte zu anderen als den genannten Zwecken findet nicht statt. 
                Wir geben Ihre Daten nur weiter, wenn Sie ausdrücklich eingewilligt haben, eine rechtliche 
                Verpflichtung besteht oder dies zur Durchsetzung unserer Rechte erforderlich ist.
              </p>
            </div>
          </div>
        </section>

        {/* User Rights */}
        <section className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <Eye className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">6. Ihre Rechte als betroffene Person</h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">6.1 Auskunftsrecht (Art. 15 DSGVO)</h3>
              <p className="text-gray-700 leading-relaxed">
                Sie haben das Recht, jederzeit Auskunft über die von uns verarbeiteten personenbezogenen Daten 
                zu erhalten. Dies umfasst Informationen über Verarbeitungszwecke, Kategorien der Daten, 
                Empfänger, Speicherdauer und Ihre Rechte.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">6.2 Recht auf Berichtigung (Art. 16 DSGVO)</h3>
              <p className="text-gray-700 leading-relaxed">
                Sie haben das Recht, die Berichtigung unrichtiger oder die Vervollständigung unvollständiger 
                personenbezogener Daten zu verlangen.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">6.3 Recht auf Löschung (Art. 17 DSGVO)</h3>
              <p className="text-gray-700 leading-relaxed">
                Sie haben das Recht, die Löschung Ihrer personenbezogenen Daten zu verlangen, sofern keine 
                gesetzlichen Aufbewahrungspflichten oder andere Rechtfertigungsgründe entgegenstehen.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">6.4 Recht auf Einschränkung (Art. 18 DSGVO)</h3>
              <p className="text-gray-700 leading-relaxed">
                Sie haben das Recht, die Einschränkung der Verarbeitung Ihrer Daten zu verlangen, wenn Sie die 
                Richtigkeit bestreiten, die Verarbeitung unrechtmäßig ist oder wir die Daten nicht mehr benötigen.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">6.5 Recht auf Datenübertragbarkeit (Art. 20 DSGVO)</h3>
              <p className="text-gray-700 leading-relaxed">
                Sie haben das Recht, die Sie betreffenden Daten in einem strukturierten, gängigen und 
                maschinenlesbaren Format zu erhalten und diese an einen anderen Verantwortlichen zu übermitteln.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">6.6 Widerspruchsrecht (Art. 21 DSGVO)</h3>
              <p className="text-gray-700 leading-relaxed">
                Sie haben das Recht, aus Gründen, die sich aus Ihrer besonderen Situation ergeben, jederzeit 
                gegen die Verarbeitung Sie betreffender personenbezogener Daten Widerspruch einzulegen.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">6.7 Widerruf der Einwilligung (Art. 7 Abs. 3 DSGVO)</h3>
              <p className="text-gray-700 leading-relaxed">
                Sie haben das Recht, eine erteilte Einwilligung jederzeit mit Wirkung für die Zukunft zu 
                widerrufen. Die Rechtmäßigkeit der bis zum Widerruf erfolgten Verarbeitung bleibt unberührt.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">6.8 Beschwerderecht (Art. 77 DSGVO)</h3>
              <p className="text-gray-700 leading-relaxed">
                Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde über die Verarbeitung Ihrer 
                Daten durch uns zu beschweren.
              </p>
            </div>
          </div>
        </section>

        {/* Data Security */}
        <section className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <Lock className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">7. Datensicherheit</h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">7.1 Technische Maßnahmen</h3>
              <p className="text-gray-700 leading-relaxed mb-2">
                Wir setzen umfassende technische und organisatorische Sicherheitsmaßnahmen ein:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>SSL/TLS-Verschlüsselung für alle Datenübertragungen</li>
                <li>Verschlüsselte Speicherung sensibler Daten</li>
                <li>Regelmäßige Sicherheitsupdates und Patches</li>
                <li>Zugriffskontrolle und Authentifizierung</li>
                <li>Regelmäßige Sicherheitsaudits</li>
                <li>Backup-Systeme und Notfallpläne</li>
              </ul>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">7.2 Organisatorische Maßnahmen</h3>
              <p className="text-gray-700 leading-relaxed">
                Zugriff auf personenbezogene Daten haben nur befugte Mitarbeiter, die zur Vertraulichkeit 
                verpflichtet wurden. Alle Mitarbeiter werden regelmäßig im Datenschutz geschult.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">7.3 Datenschutz-Folgenabschätzung</h3>
              <p className="text-gray-700 leading-relaxed">
                Für Verarbeitungsvorgänge mit hohem Risiko führen wir Datenschutz-Folgenabschätzungen gemäß 
                Art. 35 DSGVO durch und implementieren entsprechende Schutzmaßnahmen.
              </p>
            </div>
          </div>
        </section>

        {/* Data Retention */}
        <section className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <Database className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">8. Speicherdauer und Löschung</h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">8.1 Allgemeine Speicherdauer</h3>
              <p className="text-gray-700 leading-relaxed">
                Wir speichern personenbezogene Daten nur so lange, wie dies für die Erfüllung der Zwecke 
                erforderlich ist oder gesetzliche Aufbewahrungsfristen bestehen.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">8.2 Gesetzliche Aufbewahrungsfristen</h3>
              <p className="text-gray-700 leading-relaxed mb-2">
                Folgende gesetzliche Aufbewahrungsfristen sind zu beachten:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Handels- und steuerrechtliche Unterlagen: 6-10 Jahre (§§ 147 AO, 257 HGB)</li>
                <li>Vertragsunterlagen: 3 Jahre nach Vertragsende (§ 195 BGB)</li>
                <li>Kommunikationsdaten: Bis zur Erledigung des Anliegens</li>
              </ul>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">8.3 Automatisierte Löschung</h3>
              <p className="text-gray-700 leading-relaxed">
                Nach Ablauf der Aufbewahrungsfristen werden Daten automatisch gelöscht oder anonymisiert, 
                sofern keine berechtigten Gründe für eine weitere Speicherung bestehen.
              </p>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="bg-blue-50 rounded-xl p-8 mb-6 border border-blue-100">
          <div className="flex items-center gap-3 mb-4">
            <Mail className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Kontakt bei Datenschutzfragen</h2>
          </div>
          <p className="text-gray-700 leading-relaxed mb-4">
            Bei Fragen zur Verarbeitung Ihrer personenbezogenen Daten oder zur Ausübung Ihrer Rechte 
            können Sie sich jederzeit an uns wenden:
          </p>
          <div className="space-y-2 text-gray-700">
            <p><strong>Oliver Wagner</strong></p>
            <p><strong>E-Mail:</strong> oliver.wagner@gridbalance.de</p>
            <p><strong>Telefon:</strong> +49 152 23152476</p>
            <p><strong>Post:</strong> Grid Balance UG, Oliver Wagner, Rosenhügel 4a, 79599 Wittlingen</p>
          </div>
        </section>

        {/* Version Info */}
        <section className="bg-white rounded-xl shadow-sm p-8">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Versionsinformationen</h2>
          </div>
          <p className="text-gray-700 leading-relaxed">
            <strong>Stand:</strong> Januar 2026<br />
            <strong>Version:</strong> 1.0<br />
            <strong>Letzte Aktualisierung:</strong> 01.01.2026
          </p>
        </section>

      </div>
    </div>
  );
};

export default PrivacyPolicy;